import { FC, useMemo } from 'react';

import { useParams } from 'react-router-dom';

import type { IProgressBarStyleConfig } from '@appcharge/shared-ui';
import { ProgressBar } from '@appcharge/shared-ui';
import useOffersUI from 'api/useOffersUI';
import { useProduct } from 'api/useProduct';
import type { ProductsSequence } from 'common/contracts';
import {
  MOCK_PROGRESS_BAR_PRODUCTS,
  PROGRESS_BAR_DEFAULTS,
  PROGRESS_BAR_PREVIEW_DEFAULTS
} from 'views/OfferDesign/OfferDesign.consts';
import { normalizeBgColor } from 'views/OfferDesign/OfferDesign.utils';
import { OfferPreviewShell } from 'views/Offers/components/OfferPreviewShell';

import type { Product } from 'components/ProductsForm/ProductsForm.types';

interface ProgressBarPreviewProps {
  currentOfferUI: string;
  productsSequence: ProductsSequence[];
}

export const ProgressBarPreview: FC<ProgressBarPreviewProps> = ({
  currentOfferUI,
  productsSequence
}) => {
  const { publisherId } = useParams();
  const { getOfferUI } = useOffersUI(publisherId, currentOfferUI);
  const { data: productsData } = useProduct(publisherId).getProducts;

  const offerUIData = getOfferUI?.data;
  const progressBar = offerUIData?.progressBar;

  const progressBarProducts = useMemo(() => {
    const firstSequence = productsSequence?.[0];
    const firstProduct = firstSequence?.products?.[0];
    const productId = firstProduct?.productId;

    if (!firstSequence || !firstProduct || !productId) {
      return MOCK_PROGRESS_BAR_PRODUCTS;
    }

    const matchedProduct = (productsData?.result as Product[] | undefined)?.find(
      (product) => product.productId === productId
    );

    return {
      barSize: MOCK_PROGRESS_BAR_PRODUCTS.barSize,
      products: [
        {
          quantity: Number(firstProduct.quantity ?? 0),
          quantityDisplay:
            firstProduct.quantityDisplay || String(firstProduct.quantity ?? ''),
          images: {
            product:
              matchedProduct?.images?.[0]?.url ||
              MOCK_PROGRESS_BAR_PRODUCTS.products[0].images.product
          }
        }
      ]
    };
  }, [productsSequence, productsData?.result]);

  const styleConfig: IProgressBarStyleConfig = useMemo(
    () => ({
      backgroundOpacity:
        progressBar?.backgroundOpacity ??
        PROGRESS_BAR_DEFAULTS.backgroundOpacity,
      frameRadius:
        progressBar?.frameRadius ?? PROGRESS_BAR_DEFAULTS.frameRadius,
      barFillColor:
        normalizeBgColor(progressBar?.barFillColor) ||
        PROGRESS_BAR_DEFAULTS.barFillColor,
      backgroundColor:
        normalizeBgColor(progressBar?.backgroundColor) ||
        PROGRESS_BAR_DEFAULTS.backgroundColor,
      barRadius: progressBar?.barRadius ?? PROGRESS_BAR_DEFAULTS.barRadius,
      borderColor:
        normalizeBgColor(offerUIData?.borderColor) ||
        PROGRESS_BAR_DEFAULTS.borderColor,
      borderWidth:
        offerUIData?.borderWidth ?? PROGRESS_BAR_DEFAULTS.borderWidth,
      textColor: progressBar?.textColor || PROGRESS_BAR_DEFAULTS.textColor
    }),
    [progressBar, offerUIData?.borderColor, offerUIData?.borderWidth]
  );

  const firstProduct = progressBarProducts.products[0];

  return (
    <OfferPreviewShell containerClassName="flex justify-center">
      {() =>
        offerUIData && (
          <ProgressBar
            progress={
              (PROGRESS_BAR_PREVIEW_DEFAULTS.collectedPoints /
                progressBarProducts.barSize) *
              100
            }
            currentValue={PROGRESS_BAR_PREVIEW_DEFAULTS.collectedPoints}
            maxValue={progressBarProducts.barSize}
            styleConfig={styleConfig}
            leftImageUrl={progressBar?.accumulatedItem?.imageUrl || ''}
            rightImageUrl={firstProduct.images.product}
            rightLabel={firstProduct.quantityDisplay}
            isPreview
          />
        )
      }
    </OfferPreviewShell>
  );
};
